import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import logo from "./assets/image/logo_web.png";
import Banner from "./components/Banner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function handleResetPassword(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      if (!email.trim()) {
        setError(t('auth.errorEnterEmail'));
        setLoading(false);
        return;
      }

      const emailTrimmed = email.trim().toLowerCase();

      await sendPasswordResetEmail(auth, emailTrimmed);
      setSuccess(true);
      setEmail("");

      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);

      // User-friendly error messages
      if (err.code === "auth/user-not-found") {
        setError(t('auth.errorNoAccount'));
      } else if (err.code === "auth/invalid-email") {
        setError(t('auth.errorInvalidEmail'));
      } else if (err.code === "auth/too-many-requests") {
        setError(t('auth.errorTooManyRequests'));
      } else {
        setError(err.message || t('auth.errorResetFailed'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-28 -top-32 w-72 h-72 bg-red-600/30 blur-3xl rounded-full" />
        <div className="absolute right-4 top-12 w-96 h-96 bg-rose-500/20 blur-3xl rounded-full" />
        <div className="absolute -right-24 bottom-8 w-80 h-80 bg-amber-400/12 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition"
          >
            <span className="text-lg">←</span>
            {t('common.back')}
          </button>
          <Banner className="w-48 lg:w-56" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="hidden lg:block space-y-6">
            <div className="inline-flex items-center gap-3 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] text-gray-200">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Reset access
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
              {t('auth.forgotYourPassword')}
            </h1>
            <p className="text-gray-300 text-base leading-relaxed max-w-xl">
              {t('auth.forgotDescription')}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> {t('auth.secureLinkDelivery')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-400" /> {t('auth.noDowntime')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-300" /> {t('auth.fastTurnaround')}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -m-4 bg-white/5 blur-xl rounded-3xl" />
            <div className="relative bg-gray-900/70 backdrop-blur-lg border border-white/10 rounded-3xl shadow-2xl p-8 lg:p-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="BuildMyChecks Logo" className="w-12 h-12 object-contain" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-400">BuildMyChecks</p>
                    <h2 className="text-xl font-semibold text-white">{t('auth.resetPassword')}</h2>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm text-gray-300 hover:text-white transition"
                >
                  {t('nav.signIn')}
                </button>
              </div>

              {success ? (
                <div className="space-y-4">
                  <div className="bg-emerald-500/10 border border-emerald-400/40 text-emerald-100 p-4 rounded-xl">
                    <div className="font-semibold">{t('auth.checkInbox')}</div>
                    <p className="text-sm mt-1">{t('auth.resetLinkSent')}</p>
                    <p className="text-xs mt-2 text-emerald-200/80">{t('auth.redirecting')}</p>
                  </div>
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white py-3 rounded-xl font-semibold shadow-lg shadow-red-900/30 transition-transform transform hover:translate-y-[-1px]"
                  >
                    {t('auth.goToSignIn')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {error && (
                    <div className="bg-red-500/10 border border-red-400/40 text-red-100 px-4 py-3 rounded-xl text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">{t('auth.workEmail')}</label>
                    <input
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 disabled:from-red-600/60 disabled:to-rose-600/60 text-white py-3 rounded-xl font-semibold shadow-lg shadow-red-900/30 transition-transform transform hover:translate-y-[-1px] disabled:cursor-not-allowed"
                  >
                    {loading ? t('common.sending') : t('auth.sendResetLink')}
                  </button>
                </form>
              )}

              <p className="text-gray-400 text-sm mt-6 text-center">
                {t('auth.rememberPassword')}{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-red-300 hover:text-white font-semibold transition"
                >
                  {t('nav.signIn')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
